import { useState } from 'react';
import { useThemeStore } from '../../stores/theme.store';
import {
  useMembershipFees,
  useMembershipFeeSummary,
  useChargeMembershipFee,
  useChargeAllMembershipFees,
  useMembershipFeeConfig,
  useUpdateMembershipFeeConfig,
  useUnchargedAccounts,
} from '../../hooks/useMembershipFees';
import {
  CreditCard,
  Users,
  Clock,
  CheckCircle,
  AlertTriangle,
  DollarSign,
  Play,
  RefreshCw,
  Settings,
  Save,
} from 'lucide-react';

function formatCentavos(value: string | number): string {
  const num = typeof value === 'string' ? parseInt(value, 10) : value;
  return `R$ ${(num / 100).toFixed(2).replace('.', ',')}`;
}

function getCurrentCompetence(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function MembershipFeesPage() {
  const { isDarkMode } = useThemeStore();
  const [competence, setCompetence] = useState(getCurrentCompetence());
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showConfirmAll, setShowConfirmAll] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [activeTab, setActiveTab] = useState<'charged' | 'uncharged'>('charged');

  const { data: chargesData, isLoading: loadingCharges, refetch } = useMembershipFees(competence, statusFilter || undefined);
  const { data: summary, isLoading: loadingSummary } = useMembershipFeeSummary(competence);
  const { data: feeConfig, isLoading: loadingConfig } = useMembershipFeeConfig();
  const updateConfigMutation = useUpdateMembershipFeeConfig();
  const chargeMutation = useChargeMembershipFee();
  const chargeAllMutation = useChargeAllMembershipFees();
  const { data: unchargedData, isLoading: loadingUncharged, refetch: refetchUncharged } = useUnchargedAccounts(competence);

  // Config form state
  const [configForm, setConfigForm] = useState<{
    membershipFeeAmount: string;
    membershipFeeMinDepositThreshold: string;
    membershipFeeCronDay: number;
    membershipFeeAutoChargeEnabled: boolean;
  } | null>(null);

  // Initialize config form when data loads
  if (feeConfig && !configForm && !loadingConfig) {
    const amount = Number(feeConfig.membershipFeeAmount) || 2100;
    const threshold = Number(feeConfig.membershipFeeMinDepositThreshold) || 10000;
    setConfigForm({
      membershipFeeAmount: (amount / 100).toFixed(2).replace('.', ','),
      membershipFeeMinDepositThreshold: (threshold / 100).toFixed(2).replace('.', ','),
      membershipFeeCronDay: Number(feeConfig.membershipFeeCronDay) || 5,
      membershipFeeAutoChargeEnabled: feeConfig.membershipFeeAutoChargeEnabled ?? true,
    });
  }

  const handleSaveConfig = async () => {
    if (!configForm) return;
    const amountCents = Math.round(parseFloat(configForm.membershipFeeAmount.replace(',', '.')) * 100);
    const thresholdCents = Math.round(parseFloat(configForm.membershipFeeMinDepositThreshold.replace(',', '.')) * 100);
    if (amountCents <= 0 || thresholdCents <= 0 || configForm.membershipFeeCronDay < 1 || configForm.membershipFeeCronDay > 28) {
      alert('Valores inválidos. Verifique os campos.');
      return;
    }
    try {
      await updateConfigMutation.mutateAsync({
        membershipFeeAmount: amountCents,
        membershipFeeMinDepositThreshold: thresholdCents,
        membershipFeeCronDay: configForm.membershipFeeCronDay,
        membershipFeeAutoChargeEnabled: configForm.membershipFeeAutoChargeEnabled,
      });
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 3000);
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Erro ao salvar configuração');
    }
  };

  const bg = isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
  const cardBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300';

  const handleChargeAccount = async (virtualAccountId: string) => {
    try {
      await chargeMutation.mutateAsync({ virtualAccountId, competence });
      refetch();
      refetchUncharged();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Erro ao cobrar');
    }
  };

  const handleChargeAll = async () => {
    setShowConfirmAll(false);
    try {
      await chargeAllMutation.mutateAsync({ competence });
      refetch();
      refetchUncharged();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Erro ao cobrar todas');
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'PAID':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Pago</span>;
      case 'PENDING':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Pendente</span>;
      case 'FAILED':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Falhou</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  return (
    <div className={`min-h-screen ${bg} p-6`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${textPrimary}`}>Taxas de Adesão</h1>
            <p className={textSecondary}>Gestão de cobranças mensais de taxa de adesão</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => refetch()}
              className={`p-2 rounded-lg ${cardBg} border ${border} hover:opacity-80`}
              title="Atualizar"
            >
              <RefreshCw className={`w-4 h-4 ${textSecondary}`} />
            </button>
            <button
              onClick={() => setShowConfirmAll(true)}
              disabled={chargeAllMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Play className="w-4 h-4" />
              {chargeAllMutation.isPending ? 'Cobrando...' : 'Cobrar Todas'}
            </button>
          </div>
        </div>

        {/* Config Section */}
        <div className={`${cardBg} rounded-lg border ${border} mb-6`}>
          <button
            onClick={() => setShowConfig(!showConfig)}
            className={`w-full flex items-center justify-between p-4 hover:opacity-80 transition-opacity`}
          >
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-500" />
              <span className={`font-medium ${textPrimary}`}>Configuração de Cobrança Automática</span>
            </div>
            <span className={`text-sm ${textSecondary}`}>{showConfig ? '▲' : '▼'}</span>
          </button>

          {showConfig && configForm && (
            <div className={`p-4 border-t ${border}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className={`block text-xs font-medium ${textSecondary} mb-1`}>Cobrança automática</label>
                  <button
                    onClick={() => setConfigForm({ ...configForm, membershipFeeAutoChargeEnabled: !configForm.membershipFeeAutoChargeEnabled })}
                    className={`w-full flex items-center justify-between p-2.5 rounded-lg border ${border} ${configForm.membershipFeeAutoChargeEnabled ? 'bg-green-500/20 border-green-500' : inputBg}`}
                  >
                    <span className={`text-sm ${textPrimary}`}>{configForm.membershipFeeAutoChargeEnabled ? 'Habilitada' : 'Desabilitada'}</span>
                    <div className={`w-10 h-5 rounded-full transition-colors ${configForm.membershipFeeAutoChargeEnabled ? 'bg-green-500' : 'bg-gray-400'}`}>
                      <div className={`w-4 h-4 rounded-full bg-white mt-0.5 transition-transform ${configForm.membershipFeeAutoChargeEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </div>
                  </button>
                </div>
                <div>
                  <label className={`block text-xs font-medium ${textSecondary} mb-1`}>Valor da mensalidade (R$)</label>
                  <input
                    type="text"
                    value={configForm.membershipFeeAmount}
                    onChange={(e) => setConfigForm({ ...configForm, membershipFeeAmount: e.target.value })}
                    className={`w-full p-2.5 rounded-lg border text-sm ${inputBg}`}
                    placeholder="21,00"
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium ${textSecondary} mb-1`}>Depósito mínimo para cobrar (R$)</label>
                  <input
                    type="text"
                    value={configForm.membershipFeeMinDepositThreshold}
                    onChange={(e) => setConfigForm({ ...configForm, membershipFeeMinDepositThreshold: e.target.value })}
                    className={`w-full p-2.5 rounded-lg border text-sm ${inputBg}`}
                    placeholder="100,00"
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium ${textSecondary} mb-1`}>Dia do mês para CRON (1-28)</label>
                  <input
                    type="number"
                    min={1}
                    max={28}
                    value={configForm.membershipFeeCronDay}
                    onChange={(e) => setConfigForm({ ...configForm, membershipFeeCronDay: parseInt(e.target.value) || 5 })}
                    className={`w-full p-2.5 rounded-lg border text-sm ${inputBg}`}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between mt-4">
                <p className={`text-xs ${textSecondary}`}>
                  A cobrança automática desconta a mensalidade quando o correntista recebe um depósito acima do valor mínimo configurado.
                  O CRON mensal cobra as contas que não foram cobradas via depósito.
                </p>
                <button
                  onClick={handleSaveConfig}
                  disabled={updateConfigMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex-shrink-0 ml-4"
                >
                  <Save className="w-4 h-4" />
                  {updateConfigMutation.isPending ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
              {configSaved && (
                <div className="mt-2 p-2 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Configuração salva com sucesso
                </div>
              )}
            </div>
          )}
        </div>

        {/* Summary Cards */}
        {!loadingSummary && summary && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-6">
            <div className={`${cardBg} rounded-lg p-4 border ${border}`}>
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-blue-500" />
                <span className={`text-sm ${textSecondary}`}>Total Contas</span>
              </div>
              <p className={`text-2xl font-bold ${textPrimary}`}>{summary.totalAccounts}</p>
            </div>
            <div className={`${cardBg} rounded-lg p-4 border ${border}`}>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className={`text-sm ${textSecondary}`}>Cobradas</span>
              </div>
              <p className={`text-2xl font-bold ${textPrimary}`}>{summary.totalCharged}</p>
            </div>
            <div className={`${cardBg} rounded-lg p-4 border ${border}`}>
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                <span className={`text-sm ${textSecondary}`}>Pendentes</span>
              </div>
              <p className={`text-2xl font-bold ${textPrimary}`}>{summary.totalPending}</p>
            </div>
            <div className={`${cardBg} rounded-lg p-4 border ${border}`}>
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                <span className={`text-sm ${textSecondary}`}>Arrecadado</span>
              </div>
              <p className={`text-2xl font-bold text-green-500`}>{formatCentavos(summary.amountCharged)}</p>
            </div>
            <div className={`${cardBg} rounded-lg p-4 border ${border}`}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                <span className={`text-sm ${textSecondary}`}>Pendente</span>
              </div>
              <p className={`text-2xl font-bold text-yellow-500`}>{formatCentavos(summary.amountPending)}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className={`${cardBg} rounded-lg p-4 border ${border} mb-6 flex flex-wrap items-end gap-4`}>
          <div>
            <label className={`text-sm ${textSecondary} block mb-1`}>Competência</label>
            <input
              type="month"
              value={competence}
              onChange={(e) => setCompetence(e.target.value)}
              className={`px-3 py-2 rounded-lg border ${inputBg} text-sm`}
            />
          </div>
          <div>
            <label className={`text-sm ${textSecondary} block mb-1`}>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-3 py-2 rounded-lg border ${inputBg} text-sm`}
            >
              <option value="">Todos</option>
              <option value="PAID">Pago</option>
              <option value="PENDING">Pendente</option>
              <option value="FAILED">Falhou</option>
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className={`flex gap-2 mb-4`}>
          <button onClick={() => setActiveTab('charged')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === 'charged' ? 'bg-blue-600 text-white' : `${cardBg} ${textSecondary} border ${border}`}`}>
            Cobranças ({chargesData?.total ?? 0})
          </button>
          <button onClick={() => setActiveTab('uncharged')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === 'uncharged' ? 'bg-amber-600 text-white' : `${cardBg} ${textSecondary} border ${border}`}`}>
            Não Cobrados ({unchargedData?.total ?? 0})
          </button>
        </div>

        {/* Uncharged Accounts Tab */}
        {activeTab === 'uncharged' && (
          <div className={`${cardBg} rounded-lg border ${border} overflow-hidden`}>
            <div className={`px-4 py-3 border-b ${border} flex items-center justify-between`}>
              <h2 className={`font-semibold ${textPrimary}`}>Contas sem cobrança — {competence}</h2>
              <span className={`text-sm ${textSecondary}`}>{unchargedData?.total ?? 0} conta(s)</span>
            </div>
            {loadingUncharged ? (
              <div className="p-8 text-center">
                <RefreshCw className={`w-6 h-6 animate-spin mx-auto ${textSecondary}`} />
              </div>
            ) : !unchargedData?.accounts?.length ? (
              <div className="p-8 text-center">
                <CheckCircle className={`w-12 h-12 mx-auto text-green-500 opacity-50`} />
                <p className={`mt-2 ${textSecondary}`}>Todas as contas já foram cobradas para esta competência</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${border}`}>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase`}>Correntista</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase`}>CPF/CNPJ</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase`}>Conta criada em</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase`}>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unchargedData.accounts.map((acc) => (
                      <tr key={acc.virtualAccountId} className={`border-b ${border}`}>
                        <td className={`px-4 py-3 ${textPrimary}`}>
                          <div>{acc.name}</div>
                          <div className={`text-xs ${textSecondary}`}>{acc.email}</div>
                        </td>
                        <td className={`px-4 py-3 ${textSecondary} text-sm`}>{acc.cpfCnpj}</td>
                        <td className={`px-4 py-3 ${textSecondary} text-sm`}>{new Date(acc.createdAt).toLocaleDateString('pt-BR')}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleChargeAccount(acc.virtualAccountId)}
                            disabled={chargeMutation.isPending}
                            className="text-xs px-3 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                          >
                            Cobrar R$21
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Charges Table */}
        {activeTab === 'charged' && (
        <div className={`${cardBg} rounded-lg border ${border} overflow-hidden`}>
          <div className={`px-4 py-3 border-b ${border} flex items-center justify-between`}>
            <h2 className={`font-semibold ${textPrimary}`}>
              Cobranças {competence && `- ${competence}`}
            </h2>
            <span className={`text-sm ${textSecondary}`}>
              {chargesData?.total ?? 0} registro(s)
            </span>
          </div>

          {loadingCharges ? (
            <div className="p-8 text-center">
              <RefreshCw className={`w-6 h-6 animate-spin mx-auto ${textSecondary}`} />
              <p className={`mt-2 ${textSecondary}`}>Carregando...</p>
            </div>
          ) : !chargesData?.charges?.length ? (
            <div className="p-8 text-center">
              <CreditCard className={`w-12 h-12 mx-auto ${textSecondary} opacity-50`} />
              <p className={`mt-2 ${textSecondary}`}>Nenhuma cobrança encontrada para esta competência</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${border}`}>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase`}>Correntista</th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase`}>Status</th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase`}>Valor</th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase`}>Data Cobrança</th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${textSecondary} uppercase`}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {chargesData.charges.map((charge) => (
                    <tr key={charge.id} className={`border-b ${border} hover:${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                      <td className={`px-4 py-3 ${textPrimary}`}>
                        <div>{charge.accountHolder || 'N/A'}</div>
                        <div className={`text-xs ${textSecondary}`}>{charge.accountHolderEmail || ''}</div>
                      </td>
                      <td className="px-4 py-3">{statusBadge(charge.status)}</td>
                      <td className={`px-4 py-3 ${textPrimary} font-medium`}>{formatCentavos(charge.amount)}</td>
                      <td className={`px-4 py-3 ${textSecondary} text-sm`}>
                        {charge.chargedAt ? new Date(charge.chargedAt).toLocaleString('pt-BR') : '-'}
                      </td>
                      <td className="px-4 py-3">
                        {charge.status === 'PENDING' && (
                          <button
                            onClick={() => handleChargeAccount(charge.virtualAccountId)}
                            disabled={chargeMutation.isPending}
                            className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            Cobrar
                          </button>
                        )}
                        {charge.status === 'FAILED' && (
                          <span className={`text-xs ${textSecondary}`} title={charge.failureReason || ''}>
                            {charge.failureReason?.substring(0, 30) || 'Erro'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {/* Batch charge result */}
        {chargeAllMutation.data && (
          <div className={`${cardBg} rounded-lg border ${border} p-4 mt-4`}>
            <h3 className={`font-semibold ${textPrimary} mb-2`}>Resultado da Cobrança em Lote</h3>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div><span className={textSecondary}>Total:</span> <span className={textPrimary}>{chargeAllMutation.data.total}</span></div>
              <div><span className={textSecondary}>Cobradas:</span> <span className="text-green-500">{chargeAllMutation.data.charged}</span></div>
              <div><span className={textSecondary}>Pendentes:</span> <span className="text-yellow-500">{chargeAllMutation.data.pending}</span></div>
              <div><span className={textSecondary}>Puladas:</span> <span className={textPrimary}>{chargeAllMutation.data.skipped}</span></div>
            </div>
          </div>
        )}

        {/* Confirm All Modal */}
        {showConfirmAll && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className={`${cardBg} rounded-lg p-6 max-w-md w-full mx-4`}>
              <h3 className={`text-lg font-semibold ${textPrimary} mb-2`}>Confirmar Cobrança em Lote</h3>
              <p className={`${textSecondary} mb-4`}>
                Deseja cobrar a taxa de adesão de todas as contas ativas para a competência {competence}?
                Contas já cobradas serão ignoradas.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmAll(false)}
                  className={`px-4 py-2 rounded-lg border ${border} ${textPrimary} hover:opacity-80`}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleChargeAll}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
